import { NavigationRoutes } from "@/components/navigation/constants";
import { toast } from "@/hooks/use-toast";
import {
  joindreBLReceptionPartielle,
  validerReceptionPartielle,
} from "@/services/partial-reception-validation.service";
import {
  refuseArticle,
  removeRefusedArticle,
  resetPartialReception,
  savePartialReception,
  startPartialReception,
} from "@/store/reducers/partialReceptionReducer";
import { setRefreshing } from "@/store/reducers/refreshReducer";
import { RootState } from "@/store/store";
import { handleFileDownload } from "@/utils/file-download";
import {
  buildPartialReceptionPayload,
  canAccessPartialReception,
  countReception,
  findArticleToRefuse,
  haveSameIds,
  isAllAccepted,
  isAllRefused,
  isPartiallyReceived,
  listAlreadyRefusedArticles,
} from "@/utils/partial-reception";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

export const usePartialReception = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const return_ = useSelector((state: RootState) => state.return.return);
  const roles = useSelector((state: RootState) => state.authentication.roles);
  const isRefreshing = useSelector(
    (state: RootState) => state.refresh.isRefreshing
  );
  const {
    numTournee,
    refusedArticles,
    submittedRefusedIds,
    blUrl,
    isSubmitted,
  } = useSelector((state: RootState) => state.partialReception);

  const [isConfirmationOpened, setIsConfirmationOpened] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  // Reprise: l'écran est en lecture seule tant que l'utilisateur ne demande pas
  // à modifier les articles refusés
  const [isEditing, setIsEditing] = useState(false);

  const isAllowedRole = canAccessPartialReception(roles);
  const colisList = return_.colis ?? [];
  const isResumed = isPartiallyReceived(return_);
  const isReadOnly = isResumed && !isEditing;

  const counts = countReception(colisList, refusedArticles);
  const refusedIds = refusedArticles.map((refused) => refused.id);
  const hasNewRefusals = !haveSameIds(refusedIds, submittedRefusedIds);
  // Réception déjà enregistrée et inchangée: il ne reste que le BL à joindre.
  // L'url ne peut pas servir de marqueur, aucun BL n'est édité quand tout est
  // refusé ou tout accepté
  const isReceived = isSubmitted && !hasNewRefusals;
  const hasBL = blUrl !== "";

  useEffect(() => {
    if (!isAllowedRole) {
      navigate(NavigationRoutes.NOT_AUTHORIZED, { replace: true });
      return;
    }
    if (return_.numTournee === null || return_.numTournee === undefined) {
      navigate(NavigationRoutes.DASHBOARD, { replace: true });
      return;
    }
    // Un contrôle déjà en cours est conservé (retour depuis l'écran PJ)
    if (numTournee === return_.numTournee) {
      return;
    }

    dispatch(
      startPartialReception({
        numTournee: return_.numTournee,
        refusedArticles: isResumed
          ? listAlreadyRefusedArticles(colisList)
          : [],
        blUrl: isResumed ? return_.urlPathReceptionPartial ?? "" : "",
        isSubmitted: isResumed,
      })
    );
  }, []);

  const handleScan = (barcode: string) => {
    const result = findArticleToRefuse(colisList, barcode, refusedArticles);

    if (result.status === "not_found") {
      toast({
        title: "Article non trouvé",
        description: "Cet article n'appartient pas à cette tournée",
        variant: "destructive",
      });
      return;
    }
    if (result.status === "already_refused") {
      toast({
        title: "Article déjà refusé",
        variant: "destructive",
      });
      return;
    }

    dispatch(refuseArticle(result.refusedArticle));
    toast({
      title: "Article refusé",
      variant: "success",
    });
  };

  const handleRemove = (id: string) => {
    dispatch(removeRefusedArticle(id));
    toast({
      title: "Article retiré des refus",
      variant: "default",
    });
  };

  const handleBLDownload = () => {
    handleFileDownload(blUrl, `ReceptionPartielle_${return_.numTournee}`);
  };

  const goToBLJoin = () => {
    setIsConfirmationOpened(false);
    navigate(NavigationRoutes.PARTIAL_RECEPTION_PJ);
  };

  /**
   * Tournée entièrement refusée: aucun BL n'est à faire cacheter, le retour est
   * clôturé directement. Sinon on passe par l'écran PJ pour joindre le BL.
   */
  const handleBLJoin = async () => {
    if (!isAllRefused(counts)) {
      goToBLJoin();
      return;
    }

    setIsValidating(true);
    try {
      const response = await joindreBLReceptionPartielle(
        return_.numTournee.toString(),
        null,
        true
      );

      if (response?.status === 200 && response.data.success === true) {
        toast({
          title: "Retour validé",
          variant: "success",
        });
        setIsConfirmationOpened(false);
        dispatch(resetPartialReception());
        dispatch(setRefreshing(!isRefreshing));
        navigate(NavigationRoutes.DASHBOARD, { replace: true });
      } else {
        toast({
          title: "Erreur lors de la validation",
          description: response?.data?.message,
          variant: "destructive",
        });
      }
    } catch {
      toast({
        title: "Erreur lors de la validation",
        variant: "destructive",
      });
    } finally {
      setIsValidating(false);
    }
  };

  const handleReception = async () => {
    setIsValidating(true);
    try {
      const response = await validerReceptionPartielle(
        buildPartialReceptionPayload(return_.numTournee, colisList, refusedIds)
      );

      if (response?.status === 200 && response.data.success === true) {
        toast({
          title: "Réception validée",
          description: `${counts.accepted} article(s) accepté(s), ${counts.refused} refusé(s)`,
          variant: "success",
        });
        dispatch(savePartialReception(response.data.data ?? ""));
        dispatch(setRefreshing(!isRefreshing));
        setIsEditing(false);
        // Aucun refus: pas de nouveau BL à télécharger, seul le BL d'origine
        // cacheté reste à joindre
        if (isAllAccepted(counts)) {
          goToBLJoin();
        }
      } else {
        toast({
          title: "Erreur lors de la validation",
          description: response?.data?.message,
          variant: "destructive",
        });
      }
    } catch {
      toast({
        title: "Erreur lors de la validation",
        variant: "destructive",
      });
    } finally {
      setIsValidating(false);
    }
  };

  const openConfirmation = () => {
    if (isReceived && isAllAccepted(counts)) {
      goToBLJoin();
      return;
    }
    setIsConfirmationOpened(true);
  };

  return {
    return_,
    isAllowedRole,
    counts,
    refusedArticles,
    isReadOnly,
    isReceived,
    hasBL,
    isValidating,
    isConfirmationOpened,
    startEditing: () => setIsEditing(true),
    openConfirmation,
    setIsConfirmationOpened,
    handleScan,
    handleRemove,
    handleReception,
    handleBLDownload,
    handleBLJoin,
  };
};
